import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from 'react';
import { Select } from '@/shared/components/Select';
import { Textarea } from '@/shared/components/Textarea';
import { Button } from '@/shared/components/Button';
import { Badge } from '@/shared/components/Badge';
import { ApiError } from '@/lib/api-client';
import { useOppositions } from '@/features/director-professors/hooks/useSubjects';
import { BULK_INVITATION_MAX } from '@academia-apolo/shared-validators';
import { useBulkCreateStudentInvitations } from '../hooks/useStudentInvitations';
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
/**
 * Splits the raw textarea content into individual addresses. Accepts any mix of
 * new lines, commas, semicolons or spaces — directors paste from spreadsheets,
 * mail clients and notes, so we are liberal about the separators.
 */
function parseEmails(raw) {
    const tokens = raw
        .split(/[\s,;]+/)
        .map((t) => t.trim().toLowerCase())
        .filter(Boolean);
    const seen = new Set();
    const valid = [];
    const invalid = [];
    for (const token of tokens) {
        if (!EMAIL_RE.test(token)) {
            invalid.push(token);
            continue;
        }
        if (seen.has(token))
            continue;
        seen.add(token);
        valid.push(token);
    }
    return { valid, invalid };
}
export function BulkInviteStudentsForm({ onClose }) {
    const [oppositionId, setOppositionId] = useState('');
    const [raw, setRaw] = useState('');
    const [serverError, setServerError] = useState(null);
    const [summary, setSummary] = useState(null);
    const bulk = useBulkCreateStudentInvitations();
    const { data: oppositions = [], isLoading: oppLoading } = useOppositions();
    const enabledOppositions = useMemo(() => oppositions.filter((o) => o.is_enabled), [oppositions]);
    useEffect(() => {
        if (!oppositionId && enabledOppositions[0]) {
            setOppositionId(enabledOppositions[0].id);
        }
    }, [enabledOppositions, oppositionId]);
    const { valid, invalid } = useMemo(() => parseEmails(raw), [raw]);
    const overLimit = valid.length > BULK_INVITATION_MAX;
    function handleSubmit() {
        setServerError(null);
        if (!oppositionId) {
            setServerError('Selecciona una oposición.');
            return;
        }
        if (valid.length === 0) {
            setServerError('Añade al menos un email válido.');
            return;
        }
        if (overLimit) {
            setServerError(`Máximo ${BULK_INVITATION_MAX} invitaciones por lote.`);
            return;
        }
        bulk.mutate({ opposition_id: oppositionId, emails: valid }, {
            onSuccess: (result) => setSummary(result),
            onError: (err) => setServerError(messageForError(err)),
        });
    }
    // ── Result view ────────────────────────────────────────────────────────────
    if (summary) {
        return (_jsxs("div", { className: "space-y-4", children: [_jsxs("div", { className: "flex flex-wrap gap-3", children: [_jsx(SummaryStat, { label: "Total", value: summary.total, variant: "default" }), _jsx(SummaryStat, { label: "Enviadas", value: summary.sent, variant: "success" }), _jsx(SummaryStat, { label: "Omitidas", value: summary.skipped, variant: "warning" })] }), _jsx("div", { className: "max-h-72 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-700", children: _jsx("table", { className: "min-w-full text-sm", children: _jsx("tbody", { className: "divide-y divide-gray-100 dark:divide-gray-800", children: summary.results.map((r) => (_jsxs("tr", { children: [_jsx("td", { className: "px-3 py-2 font-mono text-gray-700 dark:text-gray-300", children: r.email }), _jsx("td", { className: "px-3 py-2 text-right", children: r.status === 'sent' ? (_jsx(Badge, { variant: "success", children: "Enviada" })) : (_jsxs("span", { className: "inline-flex items-center gap-2", children: [_jsx(Badge, { variant: "warning", children: "Omitida" }), _jsx("span", { className: "text-xs text-gray-500 dark:text-gray-400", children: labelForSkip(r.code, r.reason) })] })) })] }, r.email))) }) }) }), _jsxs("div", { className: "flex justify-end gap-2 pt-2", children: [_jsx(Button, { variant: "secondary", onClick: () => {
                                setSummary(null);
                                setRaw('');
                            }, children: "Enviar m\u00E1s" }), _jsx(Button, { onClick: onClose, children: "Hecho" })] })] }));
    }
    // ── Form view ────────────────────────────────────────────────────────────
    return (_jsxs("div", { className: "space-y-4", children: [_jsx("p", { className: "text-sm leading-relaxed text-gray-600 dark:text-gray-400", children: "Pega o escribe varios emails (separados por comas, espacios o saltos de l\u00EDnea). Todos se invitar\u00E1n a la misma oposici\u00F3n. Cada alumno recibir\u00E1 un enlace para completar su ficha; el enlace caduca en 7 d\u00EDas." }), _jsx(Select, { label: "Oposici\u00F3n", value: oppositionId, onChange: (e) => setOppositionId(e.target.value), options: [
                    { value: '', label: oppLoading ? 'Cargando…' : 'Selecciona…' },
                    ...enabledOppositions.map((o) => ({ value: o.id, label: o.name })),
                ] }), _jsx(Textarea, { label: "Emails de los alumnos", rows: 7, placeholder: 'ana@example.com\njuan@example.com, maria@example.com', value: raw, onChange: (e) => setRaw(e.target.value), hint: "Separadores v\u00E1lidos: salto de l\u00EDnea, coma, punto y coma o espacio." }), _jsxs("div", { className: "flex flex-wrap items-center gap-x-4 gap-y-1 text-xs", children: [_jsxs("span", { className: "text-gray-600 dark:text-gray-300", children: [_jsx("strong", { className: "tabular-nums", children: valid.length }), " email", valid.length === 1 ? '' : 's', " v\u00E1lido", valid.length === 1 ? '' : 's'] }), invalid.length > 0 && (_jsxs("span", { className: "text-amber-600 dark:text-amber-400", children: [invalid.length, " se ignorar\u00E1", invalid.length === 1 ? '' : 'n', " por formato inv\u00E1lido"] })), overLimit && (_jsxs("span", { className: "text-red-600 dark:text-red-400", children: ["Supera el m\u00E1ximo de ", BULK_INVITATION_MAX] }))] }), serverError && (_jsx("div", { className: "rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-500/40 dark:bg-red-500/10 dark:text-red-200", children: serverError })), _jsx("div", { className: "flex justify-end pt-2", children: _jsxs(Button, { onClick: handleSubmit, loading: bulk.isPending, disabled: valid.length === 0 || overLimit, children: ["Enviar ", valid.length > 0 ? `${valid.length} ` : '', "invitacion", valid.length === 1 ? '' : 'es'] }) })] }));
}
function SummaryStat({ label, value, variant, }) {
    const tone = variant === 'success'
        ? 'text-green-700 dark:text-green-300'
        : variant === 'warning'
            ? 'text-amber-700 dark:text-amber-300'
            : 'text-gray-700 dark:text-gray-200';
    return (_jsxs("div", { className: "flex-1 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 dark:border-gray-700 dark:bg-gray-800/50", children: [_jsx("p", { className: "text-[11px] uppercase tracking-wider text-gray-500 dark:text-gray-400", children: label }), _jsx("p", { className: `text-xl font-bold tabular-nums ${tone}`, children: value })] }));
}
function labelForSkip(code, reason) {
    switch (code) {
        case 'STUDENT_EMAIL_ALREADY_EXISTS':
            return 'Ya existe un usuario con ese email';
        case 'STUDENT_INVITATION_ALREADY_SENT':
            return 'Ya tenía una invitación pendiente';
        default:
            return reason ?? 'No se pudo enviar';
    }
}
function messageForError(err) {
    if (!(err instanceof ApiError))
        return 'Ha ocurrido un error.';
    switch (err.code) {
        case 'FEATURE_DISABLED':
            return 'La oposición seleccionada no está habilitada.';
        case 'VALIDATION_ERROR':
            return 'Revisa los emails: alguno no tiene un formato válido.';
        default:
            return err.message;
    }
}
//# sourceMappingURL=BulkInviteStudentsForm.js.map